// ============================================
// 数据源管理器
// 管理所有数据源适配器的生命周期
// ============================================

import type { DatasourceAdapter, RuntimeDatasource } from '../types/databinding';
import type { DatasourceConfig } from '../types/designer';
import { EventEmitter } from './EventEmitter';
import { RestAdapter } from './datasource/RestAdapter';
import { WebSocketAdapter } from './datasource/WebSocketAdapter';
import { MqttAdapter } from './datasource/MqttAdapter';
import { StaticAdapter } from './datasource/StaticAdapter';

type AdapterWithEvents = DatasourceAdapter & EventEmitter;

export class DatasourceManager extends EventEmitter {
  private adapters: Map<string, DatasourceAdapter> = new Map();
  private runtimeStates: Map<string, RuntimeDatasource> = new Map();

  /**
   * 创建适配器实例
   */
  private createAdapter(type: DatasourceConfig['type']): DatasourceAdapter {
    switch (type) {
      case 'rest': return new RestAdapter();
      case 'websocket': return new WebSocketAdapter();
      case 'mqtt': return new MqttAdapter();
      case 'static': return new StaticAdapter();
      default: throw new Error(`Unsupported datasource type: ${type}`);
    }
  }

  /**
   * 注册数据源
   */
  register(config: DatasourceConfig): void {
    if (this.adapters.has(config.id)) {
      console.warn(`Datasource ${config.id} already registered`);
      return;
    }

    const adapter = this.createAdapter(config.type) as AdapterWithEvents;
    this.adapters.set(config.id, adapter);

    const runtimeState: RuntimeDatasource = {
      id: config.id,
      config,
      status: 'disconnected',
      lastUpdate: 0,
      dataCount: 0,
    };
    this.runtimeStates.set(config.id, runtimeState);

    // 监听状态变化
    adapter.on('status:change', (status: string) => {
      const state = this.runtimeStates.get(config.id);
      if (state) {
        state.status = status as RuntimeDatasource['status'];
        this.emit('datasource:status', { id: config.id, status });
      }
    });

    adapter.on('error', (error: string) => {
      const state = this.runtimeStates.get(config.id);
      if (state) {
        state.lastError = error;
        state.status = 'error';
      }
      this.emit('datasource:error', { id: config.id, error });
    });

    adapter.on('message', (data: any) => {
      const state = this.runtimeStates.get(config.id);
      if (state) {
        state.lastUpdate = Date.now();
        state.dataCount++;
      }
      this.emit('data:update', { datasourceId: config.id, data });
    });

    this.emit('datasource:register', { id: config.id, config });
  }

  /**
   * 注销数据源
   */
  unregister(id: string): void {
    const adapter = this.adapters.get(id) as AdapterWithEvents | undefined;
    if (adapter) {
      adapter.disconnect();
      adapter.removeAllListeners();
      this.adapters.delete(id);
      this.runtimeStates.delete(id);
      this.emit('datasource:unregister', { id });
    }
  }

  /**
   * 连接数据源
   */
  async connect(id: string): Promise<void> {
    const adapter = this.adapters.get(id);
    const state = this.runtimeStates.get(id);
    if (!adapter || !state) throw new Error(`Datasource ${id} not found`);

    console.log('[DatasourceManager] connect:', id, 'type:', state.config.type);
    await adapter.connect(state.config);
    console.log('[DatasourceManager] connect: adapter connected', id);
  }

  /**
   * 断开数据源
   */
  disconnect(id: string): void {
    const adapter = this.adapters.get(id);
    if (adapter) {
      adapter.disconnect();
    }
  }

  /**
   * 订阅数据源
   */
  subscribe(id: string, topic: string, callback: (data: any) => void): void {
    const adapter = this.adapters.get(id);
    if (!adapter) throw new Error(`Datasource ${id} not found`);
    adapter.subscribe(topic, callback);
  }

  /**
   * 取消订阅
   */
  unsubscribe(id: string, topic: string): void {
    const adapter = this.adapters.get(id);
    if (adapter) {
      adapter.unsubscribe(topic);
    }
  }

  /**
   * 发布数据
   */
  publish(id: string, topic: string, data: any): void {
    const adapter = this.adapters.get(id);
    if (adapter && adapter.publish) {
      adapter.publish(topic, data);
    }
  }

  /**
   * 获取数据源状态
   */
  getStatus(id: string): RuntimeDatasource | undefined {
    return this.runtimeStates.get(id);
  }

  /**
   * 获取所有数据源状态
   */
  getAllStatuses(): RuntimeDatasource[] {
    return Array.from(this.runtimeStates.values());
  }

  /**
   * 获取适配器实例
   */
  getAdapter(id: string): DatasourceAdapter | undefined {
    return this.adapters.get(id);
  }

  /**
   * 断开所有数据源
   */
  disconnectAll(): void {
    this.adapters.forEach((adapter) => {
      adapter.disconnect();
    });
  }

  /**
   * 销毁管理器
   */
  destroy(): void {
    this.disconnectAll();
    this.adapters.forEach((adapter) => {
      (adapter as AdapterWithEvents).removeAllListeners();
    });
    this.adapters.clear();
    this.runtimeStates.clear();
    this.removeAllListeners();
  }
}
